/* ════════════════════════════════════════════════════════════
   Alerte sonore et notification à l'arrivée d'une demande de devis.

   Le son est synthétisé plutôt que chargé : pas de fichier à
   servir, rien à mettre en cache, et un timbre réglable ici même.

   Deux règles du navigateur commandent tout le reste :
   — aucun son ne peut être joué avant une interaction de
     l'utilisateur, d'où l'activation par un bouton ;
   — l'autorisation de notifier se demande sur ce même geste,
     une demande spontanée étant refusée par défaut.
   ════════════════════════════════════════════════════════════ */

const CLE = 'admin_alertes_son'

export const alertesActives = () => localStorage.getItem(CLE) === 'on'

let contexte = null

/* Un AudioContext créé hors d'un geste utilisateur naît « suspendu ».
   On le fabrique donc au moment de l'activation, et on le réveille
   à chaque usage au cas où le navigateur l'aurait mis en veille. */
function audio() {
  if (!contexte) {
    const Ctx = window.AudioContext || window.webkitAudioContext
    if (!Ctx) return null
    contexte = new Ctx()
  }
  if (contexte.state === 'suspended') contexte.resume()
  return contexte
}

/* Deux notes brèves, une quinte montante : audible sans être
   stridente, et reconnaissable au milieu des sons du système. */
export function jouerCarillon() {
  const ctx = audio()
  if (!ctx) return
  const debut = ctx.currentTime
  ;[[880, 0], [1318.5, 0.13]].forEach(([frequence, decalage]) => {
    const o = ctx.createOscillator()
    const g = ctx.createGain()
    o.type = 'sine'
    o.frequency.value = frequence
    /* Enveloppe : une attaque douce et une extinction exponentielle
       évitent le claquement d'un signal coupé net. */
    g.gain.setValueAtTime(0, debut + decalage)
    g.gain.linearRampToValueAtTime(0.22, debut + decalage + 0.015)
    g.gain.exponentialRampToValueAtTime(0.001, debut + decalage + 0.42)
    o.connect(g).connect(ctx.destination)
    o.start(debut + decalage)
    o.stop(debut + decalage + 0.45)
  })
}

/* Active ou coupe les alertes. Retourne l'état obtenu, qui n'est pas
   toujours celui demandé : l'utilisateur peut refuser les notifications
   alors que le son, lui, reste disponible. */
export async function basculerAlertes() {
  if (alertesActives()) {
    localStorage.setItem(CLE, 'off')
    return { actif: false, notifications: false }
  }

  localStorage.setItem(CLE, 'on')
  audio()          // créé sous le geste de clic : le son sera autorisé
  jouerCarillon()  // et confirme à l'oreille que c'est en marche

  let notifications = false
  if ('Notification' in window) {
    if (Notification.permission === 'granted') notifications = true
    else if (Notification.permission !== 'denied') {
      notifications = (await Notification.requestPermission()) === 'granted'
    }
  }
  return { actif: true, notifications }
}

/* Bannière système. Réservée à l'onglet en arrière-plan : quand la page
   est sous les yeux, le bandeau de l'admin suffit et une notification
   ferait doublon. */
export function notifier(titre, corps, surClic) {
  if (!('Notification' in window) || Notification.permission !== 'granted') return
  if (!document.hidden) return
  const n = new Notification(titre, {
    body: corps,
    icon: '/logo192.png',
    badge: '/favicon.png',
    tag: 'nouvelle-demande',   // une seule bannière empile les suivantes
    renotify: true,
  })
  n.onclick = () => { window.focus(); n.close(); surClic?.() }
}
